import { Schema } from "effect";

// Human-friendly workerstation ids: adjective-noun-xx, themed around vibe
// coding and startup buzzword satire. A short base36 suffix keeps collisions
// unlikely without hurting readability (e.g. "disruptive-footgun-k3").

const ADJECTIVES = [
  "disruptive",
  "revolutionary",
  "synergistic",
  "web-scale",
  "serverless",
  "frictionless",
  "hyperscale",
  "agentic",
  "vibe-coded",
  "ai-first",
  "cloud-native",
  "bleeding-edge",
  "paradigm-shifting",
  "growth-hacked",
  "10x",
  "unicorn",
  "moonshot",
  "gpt-powered",
  "yeet-driven",
  "blazingly-fast",
  "next-gen",
  "battle-tested",
  "zero-config",
  "buzzword-compliant",
  "thought-leading",
  "hallucinated",
  "prod-ready",
  "yolo-deployed"
];

const NOUNS = [
  "mvp",
  "unicorn",
  "disruptor",
  "synergy",
  "pivot",
  "grindset",
  "hustle",
  "standup",
  "monorepo",
  "microservice",
  "webhook",
  "yak-shave",
  "rug-pull",
  "side-quest",
  "vaporware",
  "hackathon",
  "boilerplate",
  "footgun",
  "spaghetti",
  "changelog",
  "roadmap",
  "backlog",
  "sprint",
  "moonshot",
  "prompt",
  "copilot",
  "iguanadon",
  "growth-loop"
];

/** Pure formatting; hosts or IdEntropy supply the three unit-interval samples. */
export function friendlyStationId([suffixSample, adjectiveSample, nounSample]: readonly [number, number, number]): string {
  const suffix = suffixSample.toString(36).slice(2, 4);
  return `${ADJECTIVES[Math.floor(adjectiveSample * ADJECTIVES.length)]}-${NOUNS[Math.floor(nounSample * NOUNS.length)]}-${suffix}`;
}

const STATION_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
const RESERVED = [
  "api",
  "query",
  "assets",
  "mcp",
  "studio",
  "favicon.png",
  // Apex "/new" mints a station. Reserved so the id pattern cannot claim it and
  // leave the route ambiguous with a station of the same name.
  "new"
] as const;

/** A usable station slug, shared by path, hostname and RPC boundaries. */
export const StationId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(STATION_ID_RE),
  Schema.makeFilter(value => !RESERVED.some(name => name === value), {
    expected: "a non-reserved station name"
  })
).pipe(Schema.brand("station")))();
export type StationId = typeof StationId.Type;

/** A path segment is a usable workerstation id (mirrors the server rule). */
export const isValidStationId: (value: string) => boolean = /* @__PURE__ */ (() => Schema.is(StationId))();

/** First path segments that belong to Workerstation routes, never to workers. */
const isReserved = /* @__PURE__ */ (() => Schema.is(Schema.Literals(RESERVED)))();
export const isReservedRouteSegment = (value: string): boolean => isReserved(value.toLowerCase());
